import type Phaser from "phaser";
import type { LookLayout } from "odyssey-shared";
import { blockPlace, blocksPerPage, lookFrameName, newBlockPlace } from "./look-plan.ts";

/**
 * The look pages (ADR-094 decision 14): square render targets holding composed looks, a block each. A look is
 * composed by clearing its block and drawing its piece sheets into it, bottom first, at the block's corner: every
 * sheet is the size of a block and shares the layout, so the block's cells are then the frames of one sprite.
 *
 * A page is made when its first block is needed and kept from then on, with a frame for every cell of every block it
 * holds, made once: a frame's name is a whole number (`lookFrameName`) and its pivot is the cell's, the point that
 * stands on the character's position, so showing a frame sets the sprite's origin with it and builds nothing.
 *
 * The pages' keys (`look-page:<n>`) are the game's own, as the sheets' are: one set of pages a game, which the
 * constructor holds a second one to.
 *
 * Composing draws from textures already on the GPU into a render target: no upload. A lost WebGL context empties
 * every page; the cache then composes its blocks again (`LookBlocks.invalidate`).
 */

/**
 * The key a look page's texture has.
 *
 * @param page The page's number.
 * @returns The key.
 */
export function lookPageKey(page: number): string {
  return `look-page:${page}`;
}

/**
 * How a sheet is drawn into a block: from its top left corner.
 */
const FROM_CORNER = Object.freeze({ originX: 0, originY: 0 });

/**
 * The look pages of one game.
 */
export class LookPages {
  /** The key of each page's texture, made or not. */
  public readonly keys: readonly string[];
  /** How many blocks a page holds. */
  public readonly perPage: number;
  private readonly textures: (Phaser.Textures.DynamicTexture | null)[];
  private readonly place = newBlockPlace();
  private count = 0;

  /**
   * @param manager The game's textures.
   * @param layout The look layout: a block's size and its cells.
   * @param pageSize A page's side in pixels.
   * @param pages How many pages there may be.
   * @throws Error when the game holds a look page already: the pages' and the sheets' texture keys are the game's, so
   *   a page has one look cache, shared by everything that draws composed characters.
   */
  public constructor(
    private readonly manager: Phaser.Textures.TextureManager,
    private readonly layout: LookLayout,
    private readonly pageSize: number,
    pages: number,
  ) {
    if (manager.exists(lookPageKey(0))) {
      throw new Error(`a page has one look cache, and ${lookPageKey(0)} exists already: share the cache that made it, or destroy it first`);
    }
    this.perPage = blocksPerPage(layout.block, pageSize);
    this.keys = Array.from({ length: pages }, (_unused, page) => lookPageKey(page));
    this.textures = this.keys.map(() => null);
  }

  /**
   * How many pages are made.
   *
   * @returns The count.
   */
  public get made(): number {
    return this.count;
  }

  /**
   * The page a block lies on.
   *
   * @param block The block's number.
   * @returns The page's number.
   */
  public pageOf(block: number): number {
    return Math.floor(block / this.perPage);
  }

  /**
   * The name of the frame showing a cell of a block, on the block's page.
   *
   * @param block The block's number.
   * @param cell The cell, its place in the layout's `cells`.
   * @returns The frame's name.
   */
  public frameOf(block: number, cell: number): number {
    return lookFrameName(block % this.perPage, cell, this.layout.cells.length);
  }

  /**
   * Makes a page if it is not made: its render target and the frames of every block it holds.
   *
   * @param page The page's number.
   * @returns The page's texture.
   * @throws Error when the texture cannot be made, or the page is past the pages there may be.
   */
  public ensure(page: number): Phaser.Textures.DynamicTexture {
    const made = this.textures[page];
    if (made !== null && made !== undefined) {
      return made;
    }
    const key = this.keys[page];
    const texture = key === undefined ? null : this.manager.addDynamicTexture(key, this.pageSize, this.pageSize);
    if (texture === null) {
      throw new Error(`look page ${page} could not be made`);
    }
    const cells = this.layout.cells;
    for (let index = 0; index < this.perPage; index += 1) {
      const place = blockPlace(index, this.layout.block, this.pageSize, this.place);
      for (let cell = 0; cell < cells.length; cell += 1) {
        const at = cells[cell] as LookLayout["cells"][number];
        const frame = texture.add(lookFrameName(index, cell, cells.length), 0, place.x + at.x, place.y + at.y, at.width, at.height);
        if (frame !== null) {
          frame.customPivot = true;
          frame.pivotX = -at.offsetX / at.width;
          frame.pivotY = -at.offsetY / at.height;
        }
      }
    }
    this.textures[page] = texture;
    this.count += 1;
    return texture;
  }

  /**
   * Composes a look into a block: clears the block and draws the look's sheets into it, bottom first.
   *
   * @param block The block's number.
   * @param sheets The texture keys of the look's sheets, bottom first, each loaded.
   */
  public compose(block: number, sheets: readonly string[]): void {
    const place = blockPlace(block, this.layout.block, this.pageSize, this.place);
    const texture = this.ensure(place.page);
    texture.clear(place.x, place.y, this.layout.block.width, this.layout.block.height);
    for (let index = 0; index < sheets.length; index += 1) {
      texture.stamp(sheets[index] as string, undefined, place.x, place.y, FROM_CORNER);
    }
    texture.render();
  }

  /**
   * Removes every page made.
   */
  public destroy(): void {
    for (let page = 0; page < this.textures.length; page += 1) {
      if (this.textures[page] !== null) {
        this.manager.remove(this.keys[page] as string);
        this.textures[page] = null;
      }
    }
    this.count = 0;
  }
}
